import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Share2 } from 'lucide-react';
import HoverBar from './HoverBar';
import { blockNonNumericPaste } from './numericInputUtils';
import DripShareModal from './DripShareModal';
import useFrozenStock from '@/hooks/useFrozenStock';
import RepinPriceButton from '@/components/RepinPriceButton';
import useCpiYoy from '@/hooks/useCpiYoy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HORIZONS = [
  { years: 5, label: '5yr' },
  { years: 10, label: '10yr' },
  { years: 20, label: '20yr' },
];

// Round 86c (May 2026) — Bumped from 3% → 5% to align with the
// Portfolio DRIP Calculator's Global Price Growth default and the
// downloadable spreadsheet's Projection tab. Lower price growth was
// understating yield-drift, making this single-stock projection
// drift higher than the spreadsheet's portfolio-level model.
const PRICE_GROWTH = 0.05; // 5% annual price growth
// Round 86c (May 2026) — Default tax rate so the DRIP loop
// reinvests AFTER-TAX dividends, matching the Portfolio DRIP
// Calculator and the spreadsheet's Projection tab. User can override
// via the new "Tax Rate" input below the Monthly Investment field.
const DEFAULT_TAX_RATE = 15;

function ResultCard({ label, value, green, testId }) {
  return (
    <div className="rounded-lg border bg-card px-2.5 py-1 text-center" data-testid={testId}>
      <p className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground leading-tight">{label}</p>
      <p className={`text-base font-bold font-mono leading-tight ${green ? 'text-green-600 dark:text-green-400' : ''}`}>{value}</p>
    </div>
  );
}

function GrowthChart({ yearlyData, isDark, disclaimer }) {
  if (!yearlyData || yearlyData.length === 0) return null;

  const maxVal = Math.max(...yearlyData.map(d => Math.max(d.portfolioValue, d.totalInvested)));
  const ceiling = Math.ceil(maxVal / 10000) * 10000;

  const yLabels = [];
  const steps = 4;
  for (let i = steps; i >= 0; i--) {
    const val = ceiling * i / steps;
    yLabels.push(`$${(val / 1000).toFixed(0)}k`);
  }

  return (
    <div className="rounded-lg border bg-card p-4" data-testid="drip-chart">
      <h3 className="text-sm font-bold mb-3">
        Portfolio Value vs Total Invested (Y1-Y{yearlyData.length})
      </h3>
      <div className="flex items-end gap-0">
        <div className="flex flex-col justify-between h-44 pr-2 shrink-0">
          {yLabels.map((label, i) => (
            <span key={i} className="text-[9px] font-mono text-muted-foreground leading-none">{label}</span>
          ))}
        </div>
        <div className="flex-1 flex items-end gap-1 h-44 border-b border-l border-border/40 pl-1">
          {yearlyData.map((d) => {
            const pvHeight = ceiling > 0 ? (d.portfolioValue / ceiling) * 100 : 0;
            const tiHeight = ceiling > 0 ? (d.totalInvested / ceiling) * 100 : 0;
            return (
              <div key={d.year} className="flex-1 flex items-end justify-center gap-[2px] h-full">
                <HoverBar
                  label={`Y${d.year} Portfolio: $${d.portfolioValue.toLocaleString()}`}
                  className="w-[45%] max-w-[24px] rounded-t-sm bg-blue-500 transition-all duration-300"
                  style={{ height: `${Math.max(pvHeight, 1)}%` }}
                  isDark={isDark}
                />
                <HoverBar
                  label={`Y${d.year} Invested: $${d.totalInvested.toLocaleString()}`}
                  className={`w-[45%] max-w-[24px] rounded-t-sm transition-all duration-300 ${isDark ? 'bg-slate-300' : 'bg-slate-500'}`}
                  style={{ height: `${Math.max(tiHeight, 1)}%` }}
                  isDark={isDark}
                />
              </div>
            );
          })}
        </div>
      </div>
      {/* X labels */}
      <div className="flex ml-8 mt-1">
        {yearlyData.map((d) => (
          <div key={d.year} className="flex-1 text-center">
            <span className="text-[8px] font-mono text-muted-foreground">Y{d.year}</span>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] font-bold text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-500 rounded-sm inline-block" /> Portfolio Value</span>
        <span className="flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm inline-block ${isDark ? 'bg-slate-300' : 'bg-slate-500'}`} /> Total Invested</span>
      </div>
      {/* Disclaimer — folded into the chart card itself so the user
          doesn't have to scroll the modal to see it. Sits right under
          the legend keys. */}
      {disclaimer && (
        <p
          className="text-[10px] font-medium text-amber-900 dark:text-neutral-400 text-center mt-1.5 leading-snug"
          data-testid="drip-disclaimer"
        >
          {disclaimer}
        </p>
      )}
    </div>
  );
}

export default function DripCalcTab({ stock: liveStock, isDark }) {
  // Phase C (May 2026) — freeze the stock snapshot on first render so
  // Massive's WebSocket minute-bar updates don't silently shift the
  // user's projection while they're typing inputs. The header price
  // chip in StockModal stays live; only THIS calculator pins the
  // input price. `repin` re-snapshots to the latest live prop.
  const [stock, repinStock] = useFrozenStock(liveStock);
  // ISO timestamp of the last snapshot capture — used by the inline
  // "Refresh price" button to show "pinned at HH:MM" in its tooltip.
  const [pinnedAt, setPinnedAt] = useState(() => new Date().toISOString());
  const handleRepin = () => {
    repinStock();
    setPinnedAt(new Date().toISOString());
  };
  const [monthlyInvest, setMonthlyInvest] = useState(500);
  const [initialShares, setInitialShares] = useState(0);
  const [horizon, setHorizon] = useState(10);
  const [divGrowthRate, setDivGrowthRate] = useState(null);
  // Round 86c (May 2026) — Tax rate input. Defaults to 15% (qualified
  // dividend bracket for most US users) and matches the Portfolio
  // DRIP Calculator's default.
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  const [shareOpen, setShareOpen] = useState(false);
  // Round 96n (Jul 3 2026) — Real vs Nominal projection toggle. When
  // "real" is on, every future-dollar figure (Portfolio Value, Annual
  // Dividends, and the yearly chart bars) is deflated by
  // (1 + CPI YoY)^year so users see purchasing-power-adjusted numbers
  // in TODAY'S dollars. CPI comes from `/api/macro` (Massive Fed).
  const cpiYoy = useCpiYoy();
  const [projectionMode, setProjectionMode] = useState(() => {
    try { return localStorage.getItem('dividrip.dripProjectionMode') || 'nominal'; } catch { return 'nominal'; }
  });
  useEffect(() => {
    try { localStorage.setItem('dividrip.dripProjectionMode', projectionMode); } catch { /* ignore */ }
  }, [projectionMode]);
  const effectiveMode = (projectionMode === 'real' && typeof cpiYoy !== 'number') ? 'nominal' : projectionMode;

  // Fetch dividend growth rate.
  //
  // Updated 2026-05 to match the user's "realistic projection" rule:
  // take the raw 5-yr CAGR and divide by 10. So a stock showing +42.1%
  // over the last 5 years projects forward at +4.21%/yr (not 42%, which
  // almost never sustains). Negatives pass straight through with the
  // same /10 damping: -20% 5-yr becomes -2%/yr. The hard upper cap is
  // 13%/yr — no stock sustains more than that long-term.
  useEffect(() => {
    if (!stock?.ticker) return;
    axios.get(`${API}/stocks/${stock.ticker}/dividend-growth`, { withCredentials: true })
      .then(res => {
        const cagr = res.data?.cagr_5yr;
        if (Number.isFinite(cagr)) {
          const damped = cagr / 10;
          // Only cap the upper end; allow negatives through so the user
          // sees the honest forward projection when a company is
          // shrinking its dividend.
          setDivGrowthRate(Math.min(13, damped));
        } else {
          // No 5-yr history available yet — default to a conservative 1%.
          setDivGrowthRate(1);
        }
      })
      .catch(() => setDivGrowthRate(1));
  }, [stock?.ticker]);

  const results = useMemo(() => {
    if (!stock?.price || !stock?.dividend_rate || divGrowthRate === null) return null;

    const price0 = stock.price;
    const annualDiv0 = stock.dividend_rate;
    // Round 86c (May 2026) — Switched from monthly to annual
    // compounding to match the spreadsheet's Projection-tab model
    // (Ending = Start × (1 + price growth) + Contribution + DRIP).
    // For high-yield positions this is significantly less aggressive
    // than monthly compounding and matches the user's mental model
    // of "this stock pays X% a year, reinvested once a year".
    // Divide CAGR-by-10 can still theoretically produce values ≤ -1.0
    // if the raw 5-yr CAGR is ≤ -1000%, which is mathematically
    // impossible but we guard anyway — `(1 + divGrowth)^year` must
    // stay positive so pow() doesn't hand us NaN or imaginary numbers.
    const divGrowthSafe = Math.max(-0.99, Number.isFinite(divGrowthRate) ? divGrowthRate / 100 : 0);
    const divGrowth = divGrowthSafe;
    const monthly = Math.max(0, monthlyInvest) || 0;
    // Initial shares the user already owns. Counted as a baseline cost
    // at today's price so the Total Return % stays meaningful (i.e.
    // we don't book pre-existing shares as "free" gains). 0 = starting
    // from scratch, which is the existing behaviour.
    const startShares = Math.max(0, initialShares) || 0;
    const baselineCost = startShares * price0;

    let totalShares = startShares;
    let currentPrice = price0;
    let totalInvested = baselineCost;
    const yearlyData = [];
    // Round 86c — DRIP reinvests AFTER-tax dividends to match the
    // spreadsheet's Projection tab (and the Portfolio DRIP Calc).
    const taxMult = 1 - (Math.max(0, Math.min(100, Number(taxRate) || 0)) / 100);

    for (let year = 1; year <= horizon; year++) {
      const currentAnnualDiv = annualDiv0 * Math.pow(1 + divGrowth, year - 1);

      // 1. Annual dividend reinvestment — DRIP at start-of-year price.
      //    Matches the spreadsheet's Projection tab exactly. Uses
      //    AFTER-tax dividend so high tax brackets correctly throttle
      //    the compounding rate.
      if (totalShares > 0) {
        const divPayment = totalShares * currentAnnualDiv * taxMult;
        const divShares = divPayment / currentPrice;
        totalShares += divShares;
      }

      // 2. Annual price growth.
      currentPrice *= (1 + PRICE_GROWTH);

      // 3. Annual contribution (monthly × 12) at post-growth price.
      if (monthly > 0) {
        const annualContrib = monthly * 12;
        const sharesBought = annualContrib / currentPrice;
        totalShares += sharesBought;
        totalInvested += annualContrib;
      }

      const portfolioValue = totalShares * currentPrice;
      const annualDividends = totalShares * currentAnnualDiv;

      yearlyData.push({
        year,
        portfolioValue: Math.round(portfolioValue),
        totalInvested: Math.round(totalInvested),
        annualDividends: Math.round(annualDividends),
        totalShares: parseFloat(totalShares.toFixed(1)),
      });
    }

    const final = yearlyData[yearlyData.length - 1];
    const totalReturn = final.totalInvested > 0
      ? ((final.portfolioValue - final.totalInvested) / final.totalInvested * 100).toFixed(1)
      : '0.0';

    // Round 96n (Jul 3 2026) — Real-dollar deflator. Divides every
    // future-dollar output by (1 + CPI YoY)^year so results render in
    // today's purchasing power. Nominal `totalInvested` is left as-is
    // because those are the actual dollars the user contributed (not
    // future dollars). This mirrors how any honest retirement calc
    // discounts back to present-value terms.
    const useReal = effectiveMode === 'real' && typeof cpiYoy === 'number';
    const cpiRate = useReal ? (cpiYoy / 100) : 0;
    const deflate = (v, year) => useReal ? v / Math.pow(1 + cpiRate, year) : v;
    const adjYearly = yearlyData.map((d) => ({
      ...d,
      portfolioValue: Math.round(deflate(d.portfolioValue, d.year)),
      annualDividends: Math.round(deflate(d.annualDividends, d.year)),
      // totalInvested stays nominal (it's a sum of actual dollars in).
    }));
    const adjFinal = adjYearly[adjYearly.length - 1];
    const adjTotalReturn = final.totalInvested > 0
      ? ((adjFinal.portfolioValue - final.totalInvested) / final.totalInvested * 100).toFixed(1)
      : '0.0';

    return {
      portfolioValue: adjFinal.portfolioValue,
      annualDividends: adjFinal.annualDividends,
      totalShares: final.totalShares,
      totalReturn: adjTotalReturn,
      yearlyData: adjYearly,
      // Keep raw nominal figures for the share modal + optional callers.
      nominalPortfolioValue: final.portfolioValue,
      nominalAnnualDividends: final.annualDividends,
      nominalTotalReturn: totalReturn,
      isReal: useReal,
      cpiUsed: useReal ? cpiYoy : null,
    };
  }, [stock, monthlyInvest, initialShares, horizon, divGrowthRate, taxRate, effectiveMode, cpiYoy]);

  if (!stock?.price) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
        No stock data available for DRIP calculation.
      </div>
    );
  }

  const displayDivGrowth = divGrowthRate !== null
    ? (Math.round(divGrowthRate * 100) / 100).toFixed(2)
    : '1.00';

  return (
    <div className="space-y-2 pb-24 sm:pb-2" data-testid="drip-calc-tab">
      {/* Round P145 (Feb 2026) — Daily-payer note. SATA-type first-of-
          cadence stocks reinvest ~252 times a year instead of annually,
          which lifts the effective APY from 13.00% to ~13.88%. The
          projection below uses annual compounding for consistency with
          every other cadence in the app, so daily-payer projections
          under-estimate slightly. Users deserve to know that. */}
      {stock?.frequency === 'Daily' && (
        <div
          className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-[11px] text-cyan-800 dark:text-cyan-200"
          data-testid="drip-daily-payer-note"
        >
          <span className="font-semibold">Daily payer note.</span>{' '}
          {stock.ticker} pays every business day (~252 payouts/yr).
          This calculator compounds annually for consistency with all
          other cadences — the real APY is slightly higher (~
          {((Math.pow(1 + (stock.dividend_yield || 0) / 100 / 252, 252) - 1) * 100).toFixed(2)}%
          vs the {(stock.dividend_yield || 0).toFixed(2)}% shown) once
          you factor in the intra-year compounding of daily DRIP.
        </div>
      )}
      {/* Round 96n (Jul 3 2026) — Real vs Nominal projection toggle.
          When "Real" is on, all future-dollar outputs are deflated to
          today's purchasing power using the latest CPI YoY. Tiny pill
          UI, mobile-first — sits above the input grid. */}
      <div className="flex items-center justify-end gap-1.5" data-testid="drip-projection-mode-toggle">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Show in:</span>
        <div className="flex items-center bg-muted rounded-md p-0.5 border border-border/60" role="tablist" aria-label="Projection dollar basis">
          <button
            type="button"
            onClick={() => setProjectionMode('nominal')}
            className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveMode === 'nominal' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            title="Show projections in NOMINAL future dollars — the raw account balance at the horizon date, ignoring inflation."
            data-testid="drip-proj-nominal"
            role="tab"
            aria-selected={effectiveMode === 'nominal'}
          >
            Nominal $
          </button>
          <button
            type="button"
            onClick={() => setProjectionMode('real')}
            disabled={typeof cpiYoy !== 'number'}
            className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${effectiveMode === 'real' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'} disabled:opacity-40 disabled:cursor-not-allowed`}
            title={
              typeof cpiYoy === 'number'
                ? `Show projections in TODAY'S dollars — every future-dollar output is discounted by (1 + ${cpiYoy.toFixed(2)}%)^year so you see purchasing power, not nominal balances.`
                : "Real-dollar view unavailable — CPI YoY data still loading. Retry in a moment."
            }
            data-testid="drip-proj-real"
            role="tab"
            aria-selected={effectiveMode === 'real'}
          >
            Today&#39;s $ {typeof cpiYoy === 'number' ? `(−${cpiYoy.toFixed(1)}%/yr)` : ''}
          </button>
        </div>
      </div>

      {/* Inputs — Round 86c: 3-column grid so Tax Rate joins the
          row alongside Monthly Investment and Time Horizon. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-0.5">Monthly Investment ($)</label>
          <input
            type="number"
            min="0"
            step="50"
            value={monthlyInvest}
            onChange={e => setMonthlyInvest(Math.max(0, Number(e.target.value) || 0))}
            onPaste={blockNonNumericPaste}
            className="w-full h-8 rounded-lg border bg-card px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="drip-monthly-input"
          />
        </div>
        <div>
          <label
            className="text-xs font-semibold text-muted-foreground block mb-0.5"
            title="Effective dividend tax rate. Used to reinvest AFTER-tax dividends inside the DRIP loop. Default 15% matches qualified-dividend treatment for most US users."
          >
            Tax Rate (%)
          </label>
          <input
            type="number"
            min="0"
            max="50"
            step="1"
            value={taxRate}
            onChange={e => setTaxRate(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
            onPaste={blockNonNumericPaste}
            className="w-full h-8 rounded-lg border bg-card px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="drip-tax-rate-input"
          />
        </div>
        <div className="col-span-2 sm:col-span-1">
          <label className="text-xs font-semibold text-muted-foreground block mb-0.5">Time Horizon</label>
          <div className="flex gap-1" data-testid="drip-horizon-buttons">
            {HORIZONS.map(h => (
              <button
                key={h.years}
                onClick={() => setHorizon(h.years)}
                data-testid={`drip-horizon-${h.years}`}
                className={`flex-1 h-8 text-sm font-medium rounded-lg transition-colors ${
                  horizon === h.years
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {h.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Initial Shares (left) + Share Projection (right) on the same row.
          Initial Shares is always shown; Share button only renders once
          results exist. The right cell uses `items-end` so the button
          bottom-aligns with the input field below the label row.
          Putting these on one row reclaims the vertical space the
          standalone share-button block used to occupy and keeps the
          modal scrollbar-free on desktop. */}
      <div className="grid grid-cols-2 gap-3 items-end">
        <div>
          <div className="flex items-center gap-3 mb-0.5" data-testid="drip-price-pin-row">
            <label className="text-xs font-semibold text-muted-foreground">
              Initial Shares <span className="text-muted-foreground/70 font-normal">(optional)</span>
            </label>
            <span className="text-[10px] font-bold text-muted-foreground">
              Calculating at <span className="font-mono font-semibold text-foreground">${Number(stock.price).toFixed(2)}</span>
            </span>
            <RepinPriceButton
              onClick={handleRepin}
              pinnedAt={pinnedAt}
              testId="drip-calc-refresh-price"
            />
          </div>
          <input
            type="number"
            min="0"
            step="1"
            value={initialShares}
            onChange={e => setInitialShares(Math.max(0, Number(e.target.value) || 0))}
            onPaste={blockNonNumericPaste}
            className="w-full h-8 rounded-lg border bg-card px-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="drip-initial-shares-input"
          />
        </div>
        {results ? (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="w-full inline-flex items-center justify-center gap-2 h-8 rounded-lg border bg-card text-sm font-semibold hover:bg-muted transition-colors"
            data-testid="drip-share-open-btn"
            title="Capture a branded summary card of this projection (just the card — nothing else on your screen)"
          >
            <Share2 className="w-4 h-4" />
            Share Projection
          </button>
        ) : (
          <div />
        )}
      </div>

      {/* Result Cards */}
      {results && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <ResultCard label="Portfolio Value" value={`$${results.portfolioValue.toLocaleString()}`} green testId="drip-portfolio-value" />
            <ResultCard label="Annual Dividends" value={`$${results.annualDividends.toLocaleString()}`} green testId="drip-annual-dividends" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ResultCard label="Total Shares" value={results.totalShares.toLocaleString()} testId="drip-total-shares" />
            <ResultCard label="Total Return" value={`${results.totalReturn}%`} green testId="drip-total-return" />
          </div>

          {/* Bar Chart — disclaimer is folded into this card's bottom
              so the modal no longer needs to scroll to surface it. */}
          <GrowthChart
            yearlyData={results.yearlyData}
            isDark={isDark}
            disclaimer={
              results.isReal
                ? `Values shown in today's dollars — deflated by CPI YoY of ${results.cpiUsed.toFixed(2)}%/yr. Nominal at Y${horizon}: $${results.nominalPortfolioValue.toLocaleString()}. Assumes ${PRICE_GROWTH * 100}% price growth + ${displayDivGrowth}% div growth + ${taxRate}% tax + DRIP reinvestment. Not financial advice.`
                : `Assumes ${PRICE_GROWTH * 100}% annual price growth + ${displayDivGrowth}% dividend growth + ${taxRate}% tax + DRIP reinvestment. Not financial advice.`
            }
          />
        </>
      )}

      <DripShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        variant="single"
        payload={{
          ticker: stock?.ticker,
          name: stock?.name,
          monthlyInvest,
          initialShares,
          horizon,
          divGrowth: Number(displayDivGrowth),
          portfolioValue: results?.portfolioValue,
          annualDividends: results?.annualDividends,
          totalShares: results?.totalShares,
          totalReturn: results?.totalReturn,
        }}
      />
    </div>
  );
}
